import { processingTasksApi } from '@/core/api/endpoints/processingTasks';
import { openSseStream } from '@/core/api/sse';
import type {
  AggregatedTaskState,
  TaskFrame,
} from '@/core/models/processingTask';
import { usePendingTaskStore } from '@/core/stores/pendingTaskStore';
import { applyFrame } from './patchApplier';

/**
 * Centralized, idempotent driver for processing tasks.
 *
 * The driver owns one SSE connection per task_id (with poll fallback). Multiple
 * UI surfaces — QuickText, RecordingSheet, PendingTaskPage, Home pending strip —
 * can `subscribe(taskId)` and read the same shared state via pendingTaskStore.
 * This avoids duplicate SSE connections and lets a task keep running even if
 * the originating screen unmounts (the user navigates away).
 *
 * Lifecycle:
 *   - subscribe(taskId, seed?) — opens a stream once, returns unsubscribe.
 *     subscribers maintain a refcount, but the driver does NOT auto-disconnect
 *     when refcount hits zero — a submitted task should keep running until it
 *     terminates regardless of UI presence. Disconnect happens only when the
 *     task reaches `completed` or `failed`.
 *   - completed tasks linger in the store for ~10s (so UI can show success
 *     and complete navigation), then are pruned automatically.
 */

const POLL_INTERVAL_MS = 5_000;
const SSE_STALL_MS = 20_000;
const COMPLETED_LINGER_MS = 10_000;

interface DriverEntry {
  taskId: string;
  state: AggregatedTaskState;
  refcount: number;
  controller: AbortController;
  lastFrameAt: number;
  pollTimer: number | null;
  stallTimer: number | null;
  lingerTimer: number | null;
  onTerminal?: (s: AggregatedTaskState) => void;
}

const drivers = new Map<string, DriverEntry>();
const terminalListeners = new Set<(s: AggregatedTaskState) => void>();

function publish(state: AggregatedTaskState): void {
  if (!state.task_id) return;
  usePendingTaskStore.getState().upsert(state);
}

function clearTimers(entry: DriverEntry): void {
  if (entry.pollTimer !== null) {
    clearInterval(entry.pollTimer);
    entry.pollTimer = null;
  }
  if (entry.stallTimer !== null) {
    clearInterval(entry.stallTimer);
    entry.stallTimer = null;
  }
}

function shutdown(entry: DriverEntry): void {
  entry.controller.abort();
  clearTimers(entry);
}

function scheduleLingerCleanup(entry: DriverEntry): void {
  if (entry.lingerTimer !== null) clearTimeout(entry.lingerTimer);
  entry.lingerTimer = window.setTimeout(() => {
    drivers.delete(entry.taskId);
    if (entry.state.status === 'completed') {
      // remove from store; UI has had a chance to show success
      usePendingTaskStore.getState().remove(entry.taskId);
    }
  }, COMPLETED_LINGER_MS);
}

function notifyTerminal(state: AggregatedTaskState): void {
  for (const fn of terminalListeners) {
    try {
      fn(state);
    } catch {
      /* ignore listener errors */
    }
  }
}

function maybeSettle(entry: DriverEntry): void {
  const s = entry.state;
  if (s.status === 'completed' || s.status === 'failed') {
    shutdown(entry);
    notifyTerminal(s);
    entry.onTerminal?.(s);
    scheduleLingerCleanup(entry);
  }
}

function applyAndPublish(entry: DriverEntry, frame: TaskFrame): void {
  entry.lastFrameAt = Date.now();
  entry.state = applyFrame(entry.state, frame);
  publish(entry.state);
  maybeSettle(entry);
}

function startPolling(entry: DriverEntry): void {
  if (entry.pollTimer !== null) return;
  entry.pollTimer = window.setInterval(async () => {
    try {
      const frame = await processingTasksApi.status(entry.taskId, entry.state.revision);
      applyAndPublish(entry, frame);
    } catch (err) {
      // surface a soft error but keep polling — transient network issues
      // shouldn't kill the task observation loop
      const msg = err instanceof Error ? err.message : String(err);
      entry.state = {
        ...entry.state,
        warning: { code: 'poll_error', message: msg },
      };
      publish(entry.state);
    }
  }, POLL_INTERVAL_MS);
}

function startStallWatchdog(entry: DriverEntry): void {
  if (entry.stallTimer !== null) return;
  entry.stallTimer = window.setInterval(() => {
    if (Date.now() - entry.lastFrameAt > SSE_STALL_MS) startPolling(entry);
  }, 5_000);
}

function openStream(entry: DriverEntry): void {
  openSseStream({
    path: processingTasksApi.streamPath(entry.taskId),
    since: entry.state.revision,
    signal: entry.controller.signal,
    onEvent: (evt) => {
      try {
        const frame = JSON.parse(evt.data) as TaskFrame;
        applyAndPublish(entry, frame);
      } catch {
        /* ignore malformed frames */
      }
    },
    onError: () => startPolling(entry),
  }).catch((err) => {
    if ((err as { name?: string }).name === 'AbortError') return;
    startPolling(entry);
  });
}

export interface SubscribeOptions {
  seed?: Partial<AggregatedTaskState>;
  onTerminal?: (s: AggregatedTaskState) => void;
}

/**
 * Idempotently subscribe to a task. If the task is already being driven, this
 * just bumps the refcount and returns the unsubscribe handle. Always publishes
 * the current state to the pendingTaskStore on subscribe so subscribers see
 * the most recent snapshot.
 */
export function subscribeTask(taskId: string, opts: SubscribeOptions = {}): () => void {
  if (!taskId) return () => {};

  let entry = drivers.get(taskId);
  if (!entry) {
    const seed = opts.seed ?? {};
    const fromStore = usePendingTaskStore.getState().tasks[taskId];
    const initial: AggregatedTaskState = {
      ...fromStore,
      ...seed,
      task_id: taskId,
      status: fromStore?.status ?? seed.status ?? 'pending',
      revision: fromStore?.revision ?? seed.revision ?? 0,
    };
    entry = {
      taskId,
      state: initial,
      refcount: 0,
      controller: new AbortController(),
      lastFrameAt: Date.now(),
      pollTimer: null,
      stallTimer: null,
      lingerTimer: null,
      onTerminal: opts.onTerminal,
    };
    drivers.set(taskId, entry);
    publish(initial);
    openStream(entry);
    startStallWatchdog(entry);
  } else if (opts.onTerminal && !entry.onTerminal) {
    entry.onTerminal = opts.onTerminal;
  }

  entry.refcount += 1;
  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    const e = drivers.get(taskId);
    if (!e) return;
    e.refcount = Math.max(0, e.refcount - 1);
    // intentional: do NOT shutdown on refcount==0; the task should keep
    // running in background. Shutdown only happens on terminal state.
  };
}

/** Listen for any task that reaches a terminal state. Used by Home to refresh. */
export function onAnyTaskTerminal(fn: (s: AggregatedTaskState) => void): () => void {
  terminalListeners.add(fn);
  return () => {
    terminalListeners.delete(fn);
  };
}

/** Returns true if the driver is actively running a stream/poll for this task. */
export function isTaskActive(taskId: string): boolean {
  const e = drivers.get(taskId);
  if (!e) return false;
  return e.state.status !== 'completed' && e.state.status !== 'failed';
}

/** Resume drivers for any non-terminal task already in the store (e.g. on app boot). */
export function resumePersistedTasks(): void {
  const tasks = usePendingTaskStore.getState().tasks;
  for (const [taskId, state] of Object.entries(tasks)) {
    if (state.status === 'completed' || state.status === 'failed') continue;
    if (drivers.has(taskId)) continue;
    subscribeTask(taskId);
  }
}
